import { supabase } from '../lib/supabase';

/**
 * Admin Service - Handles admin dashboard operations
 */

// ==================== USER MANAGEMENT ====================

/**
 * Get all users with their profiles
 */
export const getAllUsers = async () => {
  try {
    const { data, error } = await supabase?.from('users')?.select(`
        id,
        email,
        phone,
        role,
        name,
        city,
        state,
        is_verified,
        created_at,
        updated_at
      `)?.order('created_at', { ascending: false });

    if (error) throw error;

    // Transform to match UI expectations
    return data?.map(user => ({
      id: user?.id,
      name: user?.name || 'N/A',
      email: user?.email,
      role: user?.role,
      status: user?.is_verified ? 'active' : 'pending',
      registeredDate: new Date(user?.created_at)?.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      }),
      phone: user?.phone,
      city: user?.city,
      state: user?.state,
      createdAt: user?.created_at,
      updatedAt: user?.updated_at
    }));
  } catch (error) {
    console.error('Error fetching users:', error);
    throw new Error(error?.message || 'Failed to fetch users');
  }
};

/**
 * Get user by ID with full details
 */
export const getUserById = async (userId) => {
  try {
    const { data, error } = await supabase?.from('users')?.select(`
        *,
        student_profiles!student_profiles_user_id_fkey(*),
        institute_profiles!institute_profiles_user_id_fkey(*)
      `)?.eq('id', userId)?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching user:', error);
    throw new Error(error?.message || 'Failed to fetch user details');
  }
};

/**
 * Update user status (suspend/activate)
 */
export const updateUserStatus = async (userId, isVerified) => {
  try {
    const { data, error } = await supabase?.from('users')?.update({ is_verified: isVerified, updated_at: new Date()?.toISOString() })?.eq('id', userId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating user status:', error);
    throw new Error(error?.message || 'Failed to update user status');
  }
};

/**
 * Delete user (soft delete by setting is_verified to false)
 */
export const deleteUser = async (userId) => {
  try {
    // In production, you might want to implement soft delete or archive
    const { error } = await supabase?.from('users')?.delete()?.eq('id', userId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error deleting user:', error);
    throw new Error(error?.message || 'Failed to delete user');
  }
};

/**
 * Update user details
 */
export const updateUser = async (userId, userData) => {
  try {
    const { name, email, phone, city, state, role, status } = userData;
    
    // Map status to is_verified
    const isVerified = status === 'active';
    
    const { data, error } = await supabase?.from('users')?.update({
      name,
      email,
      phone,
      city,
      state,
      role,
      is_verified: isVerified,
      updated_at: new Date()?.toISOString()
    })?.eq('id', userId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating user:', error);
    throw new Error(error?.message || 'Failed to update user');
  }
};

/**
 * Bulk suspend users
 */
export const bulkSuspendUsers = async (userIds) => {
  try {
    const { data, error } = await supabase?.from('users')?.update({ is_verified: false, updated_at: new Date()?.toISOString() })?.in('id', userIds)?.select();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error suspending users:', error);
    throw new Error(error?.message || 'Failed to suspend users');
  }
};

/**
 * Bulk delete users
 */
export const bulkDeleteUsers = async (userIds) => {
  try {
    const { error } = await supabase?.from('users')?.delete()?.in('id', userIds);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error deleting users:', error);
    throw new Error(error?.message || 'Failed to delete users');
  }
};

/**
 * Reset user password
 */
export const resetPassword = async (userId, userEmail) => {
  try {
    // Generate a temporary password (8 characters: letters + numbers)
    const generateTempPassword = () => {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
      let password = '';
      for (let i = 0; i < 8; i++) {
        password += chars?.charAt(Math.floor(Math.random() * chars?.length));
      }
      return password;
    };

    const tempPassword = generateTempPassword();
    
    // Hash the password using bcrypt
    const bcrypt = await import('bcryptjs');
    const saltRounds = 10;
    const passwordHash = await bcrypt?.hash(tempPassword, saltRounds);

    // Update user's password in database
    const { data, error } = await supabase?.from('users')?.update({
      password_hash: passwordHash,
      updated_at: new Date()?.toISOString()
    })?.eq('id', userId)?.select()?.single();

    if (error) throw error;

    // TODO: Send email with temporary password
    // For now, log it (in production, use email service)
    console.log(`Temporary password for ${userEmail}: ${tempPassword}`);
    
    // In production, you would call an email service here:
    // await sendPasswordResetEmail(userEmail, tempPassword);

    return { success: true, tempPassword };
  } catch (error) {
    console.error('Error resetting password:', error);
    throw new Error(error?.message || 'Failed to reset password');
  }
};

/**
 * Get all students with approval status
 */
export const getAllStudents = async () => {
  try {
    const { data, error } = await supabase?.from('users')?.select(`
        id,
        email,
        phone,
        name,
        city,
        state,
        is_verified,
        created_at,
        student_profiles!student_profiles_user_id_fkey(
          course_interest,
          exam_status,
          budget_range,
          study_mode,
          approval_status,
          approved_at,
          approved_by,
          is_restricted,
          restricted_at,
          restricted_by
        )
      `)?.eq('role', 'student')?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(student => ({
      id: student?.id,
      name: student?.name || 'N/A',
      email: student?.email,
      phone: student?.phone,
      city: student?.city,
      state: student?.state,
      courseInterest: student?.student_profiles?.[0]?.course_interest,
      examStatus: student?.student_profiles?.[0]?.exam_status,
      budgetRange: student?.student_profiles?.[0]?.budget_range,
      studyMode: student?.student_profiles?.[0]?.study_mode,
      approvalStatus: student?.student_profiles?.[0]?.approval_status || 'pending',
      approvedAt: student?.student_profiles?.[0]?.approved_at,
      approvedBy: student?.student_profiles?.[0]?.approved_by,
      isRestricted: student?.student_profiles?.[0]?.is_restricted === true,
      restrictedAt: student?.student_profiles?.[0]?.restricted_at,
      restrictedBy: student?.student_profiles?.[0]?.restricted_by,
      registeredDate: new Date(student?.created_at)?.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      }),
      createdAt: student?.created_at
    }));
  } catch (error) {
    console.error('Error fetching students:', error);
    throw new Error(error?.message || 'Failed to fetch students');
  }
};

/**
 * Restrict student access (super admin only)
 */
export const restrictStudent = async (studentUserId, adminUserId) => {
  try {
    const { error } = await supabase
      ?.from('student_profiles')
      ?.update({
        is_restricted: true,
        restricted_at: new Date()?.toISOString(),
        restricted_by: adminUserId
      })
      ?.eq('user_id', studentUserId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error restricting student:', error);
    throw new Error(error?.message || 'Failed to restrict student access');
  }
};

/**
 * Unrestrict student access (super admin only)
 */
export const unrestrictStudent = async (studentUserId) => {
  try {
    const { error } = await supabase
      ?.from('student_profiles')
      ?.update({
        is_restricted: false,
        restricted_at: null,
        restricted_by: null
      })
      ?.eq('user_id', studentUserId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error unrestricting student:', error);
    throw new Error(error?.message || 'Failed to restore student access');
  }
};

/**
 * Approve student
 */
export const approveStudent = async (studentUserId, adminUserId) => {
  try {
    const { error } = await supabase?.rpc('approve_student', {
      student_user_id: studentUserId,
      admin_user_id: adminUserId
    });

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error approving student:', error);
    throw new Error(error?.message || 'Failed to approve student');
  }
};

/**
 * Reject student
 */
export const rejectStudent = async (studentUserId, adminUserId) => {
  try {
    const { error } = await supabase?.rpc('reject_student', {
      student_user_id: studentUserId,
      admin_user_id: adminUserId
    });

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error rejecting student:', error);
    throw new Error(error?.message || 'Failed to reject student');
  }
};

/**
 * Get pending student approvals count
 */
export const getPendingStudentApprovalsCount = async () => {
  try {
    const { count, error } = await supabase?.from('student_profiles')?.select('*', { count: 'exact', head: true })?.eq('approval_status', 'pending');

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error fetching pending approvals count:', error);
    return 0;
  }
};

// ==================== INSTITUTE MANAGEMENT ====================

/**
 * Get pending institute approvals
 */
export const getPendingInstitutes = async () => {
  try {
    const { data, error } = await supabase?.from('institute_profiles')?.select(`
        id,
        user_id,
        institute_name,
        logo_url,
        description,
        website,
        contact_person,
        approval_status,
        booth_type,
        created_at,
        users!inner(email, phone, city, state)
      `)?.eq('approval_status', 'pending')?.order('created_at', { ascending: false });

    if (error) throw error;

    // Get course count for each institute
    const institutesWithCourses = await Promise.all(
      data?.map(async (institute) => {
        const { count } = await supabase?.from('courses')?.select('*', { count: 'exact', head: true })?.eq('institute_id', institute?.id);

        return {
          id: institute?.id,
          userId: institute?.user_id,
          name: institute?.institute_name,
          logo: institute?.logo_url || 'https://img.rocket.new/generatedImages/rocket_gen_img_default.png',
          logoAlt: `${institute?.institute_name} logo`,
          description: institute?.description || 'No description available',
          location: `${institute?.users?.city || 'N/A'}, ${institute?.users?.state || 'N/A'}`,
          coursesCount: count || 0,
          appliedDate: new Date(institute?.created_at)?.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
          }),
          boothType: institute?.booth_type?.charAt(0)?.toUpperCase() + institute?.booth_type?.slice(1),
          email: institute?.users?.email,
          phone: institute?.users?.phone,
          website: institute?.website,
          contactPerson: institute?.contact_person
        };
      })
    );

    return institutesWithCourses;
  } catch (error) {
    console.error('Error fetching pending institutes:', error);
    throw new Error(error?.message || 'Failed to fetch pending institutes');
  }
};

/**
 * Approve institute
 */
export const approveInstitute = async (instituteId) => {
  try {
    const { data, error } = await supabase?.from('institute_profiles')?.update({ 
        approval_status: 'approved',
        updated_at: new Date()?.toISOString()
      })?.eq('id', instituteId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error approving institute:', error);
    throw new Error(error?.message || 'Failed to approve institute');
  }
};

/**
 * Reject institute
 */
export const rejectInstitute = async (instituteId) => {
  try {
    const { data, error } = await supabase?.from('institute_profiles')?.update({ 
        approval_status: 'rejected',
        updated_at: new Date()?.toISOString()
      })?.eq('id', instituteId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error rejecting institute:', error);
    throw new Error(error?.message || 'Failed to reject institute');
  }
};

/**
 * Get institute details by ID
 */
export const getInstituteDetails = async (instituteId) => {
  try {
    const { data, error } = await supabase?.from('institute_profiles')?.select(`
        *,
        users!inner(*),
        courses(count)
      `)?.eq('id', instituteId)?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching institute details:', error);
    throw new Error(error?.message || 'Failed to fetch institute details');
  }
};

/**
 * Update institute profile details
 */
export const updateInstituteProfile = async (instituteId, profileData) => {
  try {
    const updatePayload = {
      updated_at: new Date()?.toISOString(),
    };
    if (profileData?.institute_name !== undefined) updatePayload.institute_name = profileData?.institute_name;
    if (profileData?.description !== undefined) updatePayload.description = profileData?.description;
    if (profileData?.address !== undefined) updatePayload.address = profileData?.address;
    if (profileData?.city !== undefined) updatePayload.city = profileData?.city;
    if (profileData?.state !== undefined) updatePayload.state = profileData?.state;
    if (profileData?.country !== undefined) updatePayload.country = profileData?.country;
    if (profileData?.postal_code !== undefined) updatePayload.postal_code = profileData?.postal_code;
    if (profileData?.phone !== undefined) updatePayload.phone = profileData?.phone;
    if (profileData?.website !== undefined) updatePayload.website = profileData?.website;
    if (profileData?.contact_person !== undefined) updatePayload.contact_person = profileData?.contact_person;
    if (profileData?.established_year !== undefined) updatePayload.established_year = profileData?.established_year ? parseInt(profileData?.established_year) : null;
    if (profileData?.accreditation !== undefined) updatePayload.accreditation = profileData?.accreditation;
    if (profileData?.type !== undefined) updatePayload.type = profileData?.type;

    const { data, error } = await supabase
      ?.from('institute_profiles')
      ?.update(updatePayload)
      ?.eq('id', instituteId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating institute profile:', error);
    throw new Error(error?.message || 'Failed to update institute profile');
  }
};

// ==================== INSTITUTE ACCOUNT REQUESTS ====================

/**
 * Get all institute account requests
 */
export const getInstituteAccountRequests = async () => {
  try {
    const { data, error } = await supabase
      ?.from('institute_account_requests')
      ?.select('*')
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching institute account requests:', error);
    throw new Error(error?.message || 'Failed to fetch institute account requests');
  }
};

/**
 * Update status of an institute account request
 */
export const updateInstituteAccountRequestStatus = async (requestId, status) => {
  try {
    const { data, error } = await supabase
      ?.from('institute_account_requests')
      ?.update({ status, updated_at: new Date()?.toISOString() })
      ?.eq('id', requestId)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating request status:', error);
    throw new Error(error?.message || 'Failed to update request status');
  }
};

// ==================== EVENT CONFIGURATION ====================

/**
 * Save event configuration
 * Note: This creates a new table for event_config if it doesn't exist * For now, we'll use localStorage as a simple solution
 */
export const saveEventConfig = async (config) => {
  try {
    // Store in localStorage for now
    // In production, you'd want to create an event_config table
    localStorage.setItem('event_config', JSON.stringify({
      ...config,
      updatedAt: new Date()?.toISOString()
    }));

    return { success: true, config };
  } catch (error) {
    console.error('Error saving event config:', error);
    throw new Error(error?.message || 'Failed to save event configuration');
  }
};

/**
 * Get event configuration
 */
export const getEventConfig = async () => {
  try {
    const stored = localStorage.getItem('event_config');
    if (stored) {
      return JSON.parse(stored);
    }
    
    // Return default config
    return {
      eventName: 'Virtual Education Fair 2026',
      eventDate: '2026-02-15',
      startTime: '09:00',
      endTime: '18:00',
      sessionTypes: ['chat', 'video', 'presentation'],
      maxSessions: 50,
      sessionDuration: 30,
      chatStartTime: '09:00',
      chatEndTime: '18:00',
      enableRegistration: true,
      enableBooths: true,
      enableVideo: true,
      enableAnalytics: true
    };
  } catch (error) {
    console.error('Error fetching event config:', error);
    throw new Error(error?.message || 'Failed to fetch event configuration');
  }
};

/**
 * Export data to CSV format
 */
export const exportDataToCSV = async (reportConfig) => {
  try {
    let data = [];
    let filename = 'export';
    
    // Fetch data based on report type
    switch (reportConfig?.type) {
      case 'registrations':
        const { data: users } = await supabase?.from('users')?.select('id, email, name, role, city, state, created_at')?.order('created_at', { ascending: false });
        data = users || [];
        filename = 'registrations';
        break;
        
      case 'institute_analytics':
        const { data: institutes } = await supabase?.from('institute_profiles')?.select('id, institute_name, approval_status, booth_type, created_at')?.order('created_at', { ascending: false });
        data = institutes || [];
        filename = 'institutes';
        break;
        
      case 'student_activity':
        const { data: students } = await supabase?.from('student_profiles')?.select('*, users!inner(email, name, city, state)')?.order('users.created_at', { ascending: false });
        data = students?.map(s => ({
          email: s?.users?.email,
          name: s?.users?.name,
          city: s?.users?.city,
          state: s?.users?.state,
          course_interest: s?.course_interest,
          exam_status: s?.exam_status,
          budget_range: s?.budget_range
        })) || [];
        filename = 'students';
        break;
        
      case 'engagement':
        const { data: sessions } = await supabase?.from('counseling_sessions')?.select('id, session_type, scheduled_at, status, duration_minutes')?.order('scheduled_at', { ascending: false });
        data = sessions || [];
        filename = 'engagement';
        break;
        
      case 'comprehensive':
        // Get all data types
        const [usersRes, institutesRes, sessionsRes] = await Promise.all([
          supabase?.from('users')?.select('*')?.order('created_at', { ascending: false }),
          supabase?.from('institute_profiles')?.select('*')?.order('created_at', { ascending: false }),
          supabase?.from('counseling_sessions')?.select('*')?.order('scheduled_at', { ascending: false })
        ]);
        
        data = {
          users: usersRes?.data || [],
          institutes: institutesRes?.data || [],
          sessions: sessionsRes?.data || []
        };
        filename = 'comprehensive_report';
        break;
        
      default:
        throw new Error('Invalid report type');
    }
    
    // Apply date filters if provided
    if (reportConfig?.dateFrom || reportConfig?.dateTo) {
      if (Array.isArray(data)) {
        data = data?.filter(item => {
          const itemDate = new Date(item?.created_at || item?.scheduled_at);
          const fromDate = reportConfig?.dateFrom ? new Date(reportConfig?.dateFrom) : null;
          const toDate = reportConfig?.dateTo ? new Date(reportConfig?.dateTo) : null;
          
          if (fromDate && itemDate < fromDate) return false;
          if (toDate && itemDate > toDate) return false;
          return true;
        });
      }
    }
    
    // Convert to CSV
    if (reportConfig?.format === 'csv' || reportConfig?.format === 'excel') {
      let csv = '';
      
      if (reportConfig?.type === 'comprehensive') {
        // Handle comprehensive report with multiple sheets
        csv = '=== USERS ===\n';
        csv += convertToCSV(data?.users);
        csv += '\n\n=== INSTITUTES ===\n';
        csv += convertToCSV(data?.institutes);
        csv += '\n\n=== SESSIONS ===\n';
        csv += convertToCSV(data?.sessions);
      } else {
        csv = convertToCSV(data);
      }
      
      // Download CSV file
      downloadCSV(csv, `${filename}_${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
      return { success: true, message: 'Data exported successfully' };
    }
    
    throw new Error('Unsupported export format');
  } catch (error) {
    console.error('Error exporting data:', error);
    throw error;
  }
};

/**
 * Get admin dashboard metrics from real data
 */
export const getAdminMetrics = async () => {
  try {
    const [usersRes, boothVisitsRes, sessionsRes, studentsRes] = await Promise.all([
      supabase?.from('users')?.select('id', { count: 'exact', head: true }),
      supabase?.from('booth_visits')?.select('id', { count: 'exact', head: true }),
      supabase?.from('counseling_sessions')?.select('id', { count: 'exact', head: true }),
      supabase?.from('student_profiles')?.select('id', { count: 'exact', head: true })?.eq('approval_status', 'approved')
    ]);
    return {
      totalRegistrations: usersRes?.count || 0,
      activeUsers: studentsRes?.count || 0,
      boothVisits: boothVisitsRes?.count || 0,
      videoSessions: sessionsRes?.count || 0
    };
  } catch (error) {
    console.error('Error fetching admin metrics:', error);
    return { totalRegistrations: 0, activeUsers: 0, boothVisits: 0, videoSessions: 0 };
  }
};

/**
 * Get recent platform activities
 */
export const getRecentActivities = async () => {
  try {
    const { data: recentUsers } = await supabase
      ?.from('users')
      ?.select('id, name, role, created_at')
      ?.order('created_at', { ascending: false })
      ?.limit(5);

    return (recentUsers || [])?.map((u, i) => ({
      id: u?.id || i,
      type: u?.role === 'institute' ? 'approval' : 'registration',
      title: u?.role === 'institute' ? 'New Institute Registered' : 'New Student Registration',
      description: `${u?.name || 'User'} joined as ${u?.role}`,
      timestamp: new Date(u?.created_at),
      user: u?.name || 'User'
    }));
  } catch (error) {
    console.error('Error fetching activities:', error);
    return [];
  }
};

/**
 * Get registration trend data (last 7 days)
 */
export const getRegistrationTrend = async () => {
  try {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date?.setDate(date?.getDate() - i);
      days?.push(date?.toISOString()?.split('T')?.[0]);
    }

    const { data } = await supabase
      ?.from('users')
      ?.select('created_at')
      ?.gte('created_at', days?.[0])
      ?.order('created_at', { ascending: true });

    return days?.map(day => ({
      name: new Date(day)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      value: (data || [])?.filter(u => u?.created_at?.startsWith(day))?.length
    }));
  } catch (error) {
    console.error('Error fetching registration trend:', error);
    return [];
  }
};

/**
 * Get engagement distribution data
 */
export const getEngagementData = async () => {
  try {
    const [chatsRes, sessionsRes, visitsRes] = await Promise.all([
      supabase?.from('chat_messages')?.select('id', { count: 'exact', head: true }),
      supabase?.from('counseling_sessions')?.select('id', { count: 'exact', head: true }),
      supabase?.from('booth_visits')?.select('id', { count: 'exact', head: true })
    ]);
    return [
      { name: 'Chat', value: chatsRes?.count || 0 },
      { name: 'Video', value: sessionsRes?.count || 0 },
      { name: 'Booth Visits', value: visitsRes?.count || 0 }
    ];
  } catch (error) {
    console.error('Error fetching engagement data:', error);
    return [];
  }
};

/**
 * Get booth visits trend data (last 7 days)
 */
export const getBoothVisitsTrend = async () => {
  try {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date?.setDate(date?.getDate() - i);
      days?.push(date?.toISOString()?.split('T')?.[0]);
    }

    const { data } = await supabase
      ?.from('booth_visits')
      ?.select('created_at')
      ?.gte('created_at', days?.[0])
      ?.order('created_at', { ascending: true });

    return days?.map(day => ({
      name: new Date(day)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      value: (data || [])?.filter(v => v?.created_at?.startsWith(day))?.length
    }));
  } catch (error) {
    console.error('Error fetching booth visits trend:', error);
    return [];
  }
};

/**
 * Convert array of objects to CSV string
 */
function convertToCSV(data) {
  if (!data || data?.length === 0) {
    // Return empty CSV with appropriate headers based on common data structure
    return 'id,email,name,role,city,state,created_at\nNo data available';
  }
  
  const headers = Object.keys(data?.[0]);
  const csvRows = [];
  
  // Add header row
  csvRows?.push(headers?.join(','));
  
  // Add data rows
  for (const row of data) {
    const values = headers?.map(header => {
      const value = row?.[header];
      // Escape commas and quotes
      const escaped = ('' + value)?.replace(/"/g, '""');
      return `"${escaped}"`;
    });
    csvRows?.push(values?.join(','));
  }
  
  return csvRows?.join('\n');
}

/**
 * Download CSV file
 */
function downloadCSV(csv, filename) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link?.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link?.setAttribute('href', url);
    link?.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  }
}

export const adminService = {
  getAllUsers,
  getUserById,
  updateUser,
  updateUserStatus,
  deleteUser,
  bulkSuspendUsers,
  bulkDeleteUsers,
  getPendingInstitutes,
  approveInstitute,
  rejectInstitute,
  getInstituteDetails,
  updateInstituteProfile,
  getInstituteAccountRequests,
  updateInstituteAccountRequestStatus,
  saveEventConfig,
  getEventConfig,
  resetPassword,
  exportDataToCSV,
  getAdminMetrics,
  getRecentActivities,
  getRegistrationTrend,
  getEngagementData,
  getBoothVisitsTrend
};

export default adminService;